/* -----------------Globals--------------- */
import { isWeb } from "@helpers/platform";

import { MaterialCommunityIcons as Icon } from "@expo/vector-icons";
/* -----------------UI--------------- */
import {
	Card,
	Checkbox,
	Chip,
	IconButton,
	List,
	RadioButton,
} from "react-native-paper";

/* -----------------Helpers & Hooks--------------- */
import { useAppTheme } from "@hooks/useAppTheme";

import { useAppStore } from "@hooks/useAppStore";
import Box from "@memorang/ui/src/components/Box";
import TooltipWrapper from "@memorang/ui/src/components/TooltipWrapper";
/* -----------------Child components--------------- */
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import type { ViewStyle } from "react-native";
import { ListItemContainer } from "./ListItemContainer";

type Props = {
	answered?: boolean;
	stem?: string;
	handleSelectChoice: (crossingOut?: boolean) => void;
	status: "checked" | "unchecked";
	multiSelect?: boolean;
	value?: string;
	isCrossedOut?: boolean;
	isCorrectAnswer?: boolean;
	percentageAnswered?: number;
	useCardWrapper?: boolean;
	showCrossOut?: boolean;
	isSelected?: boolean;
	index: number;
	drag?: () => void;
	disabled?: boolean;
};
const ChoiceListItem = ({
	answered,
	disabled,
	stem,
	handleSelectChoice,
	status,
	multiSelect,
	value,
	isCrossedOut,
	isCorrectAnswer,
	percentageAnswered,
	useCardWrapper = true,
	showCrossOut,
	isSelected = false,
	index,
	drag,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
}: Props) => {
	const theme = useAppTheme();

	const appFont = useAppStore((store) => store.app.font) || "opensans";

	//TODO:The label props on the Checkbox.Item or RadioBox does not accept a component. It only accepts a string.
	// So we have to create custom component for the Checkbox and RadioBox.

	const SelectionContainer = multiSelect
		? Checkbox.Android
		: RadioButton.Android;
	const showLeft = isWeb || multiSelect || !useCardWrapper;

	const getCardProps = () => {
		const answerType = isCorrectAnswer ? "correct" : "incorrect";

		if (isSelected) {
			const backgroundColor = answered
				? theme.colors.answer[answerType].background
				: theme.colors.secondaryContainer;
			const borderColor = answered
				? theme.colors.answer[answerType].border
				: theme.colors.secondary;
			return {
				backgroundColor,
				borderColor,
			};
		}

		if (isCorrectAnswer && answered) {
			return {
				backgroundColor: theme.colors.answer[answerType].background,
				borderColor: theme.colors.answer[answerType].border,
			};
		}

		return {
			backgroundColor: undefined,
			borderColor: theme.colors.border,
		};
	};

	const getTextColor = () => {
		if (isSelected || (isCorrectAnswer && answered)) {
			return answered ? theme.colors.shadow : theme.colors.text;
		}

		return theme.colors.text;
	};

	const mode = "outlined";

	const CrossOutAnswerIconButton = (props: {
		color: string;
		style?: ViewStyle | undefined;
		isCrossedOut?: boolean;
	}) => (
		<IconButton
			{...props}
			disabled={answered || disabled}
			onPress={() => {
				if (!answered) {
					handleSelectChoice(true);
				}
			}}
			iconColor={theme.colors.onSurfaceVariant}
			icon={(props) => <Icon {...props} name="close" />}
			size={24}
			testID={`cross-out-answer-icon-button-${index}`}
			style={{
				marginRight: -6,
				...(isCrossedOut ? { opacity: 0.38 } : {}),
			}}
		/>
	);

	const chipIcon = isSelected
		? isCorrectAnswer
			? "check"
			: "close"
		: undefined;
	const chipColor = isCorrectAnswer
		? theme.colors.confidence.high
		: theme.colors.confidence.low;

	const PercentageAnsweredChip = ({ value }: { value?: number }) => {
		const tooltip = isSelected
			? `You selected this ${
					isCorrectAnswer ? "correct" : "incorrect"
				} choice along with ${value}% of other people`
			: `${value}% of people selected this ${
					isCorrectAnswer ? "correct" : "incorrect"
				} choice`;
		return (
			<TooltipWrapper title={tooltip}>
				<Chip
					mode="outlined"
					compact
					selected={isSelected}
					icon={() => <Icon name={chipIcon} size={18} color={chipColor} />}
					textStyle={{ color: chipColor, fontFamily: `${appFont}-semibold` }}
					selectedColor={chipColor}
				>
					{value != null && value >= 10 ? `${value}%` : "< 1%"}
				</Chip>
			</TooltipWrapper>
		);
	};

	const Wrapper = useCardWrapper ? Card : Box;

	const cardProps = getCardProps();

	const fontSizes = useAppStore((store) => store.app.fontSizes);

	const fontSize = fontSizes?.answer || 14;

	return (
		<Wrapper
			mode={mode}
			style={{
				...(useCardWrapper ? cardProps : {}),
				opacity: disabled && !answered ? 0.5 : 1,
				// borderWidth: 1,
			}}
		>
			<ListItemContainer
				handleCrossOut={() => {
					if (!answered) {
						handleSelectChoice(true);
					}
				}}
			>
				<List.Item
					onResponderStart={drag}
					disabled={answered || disabled}
					accessibilityLabel={`Choice ${index}. ${stem}`}
					accessible
					style={{
						paddingLeft: useCardWrapper && isWeb ? 6 : 0,
						paddingRight: useCardWrapper ? 12 : 0,
						// borderRadius: theme.roundness,
						paddingVertical: useCardWrapper ? 8 : 0,
						cursor: answered ? "auto" : "pointer",
						overflow: "hidden",
					}}
					left={(p) =>
						showLeft ? (
							<Box
								flexDirection="row"
								alignItems="center"
								cursor={answered ? "default" : "pointer"}
							>
								{!useCardWrapper && answered && (
									<Icon
										name={isCorrectAnswer ? "check-circle" : "close-circle"}
										size={24}
										color={chipColor}
										disabled
									/>
								)}
								<SelectionContainer
									{...p}
									value={value!}
									status={status}
									accessibilityLabel={`Choice ${index}`}
									aria-checked={isSelected}
									color={cardProps.borderColor || theme.colors.onSurfaceVariant}
									disabled={answered && !isSelected}
									onPress={() => {
										if (!answered) {
											handleSelectChoice();
										}
									}}
								/>
							</Box>
						) : null
					}
					onPress={() => handleSelectChoice()}
					titleNumberOfLines={10}
					title={() => (
						<CustomMarkdownRenderer
							text={stem}
							variant="answer"
							customStyles={{
								text: {
									color: theme.colors.text,
								},
								paragraph: {
									fontSize,
									...(isCrossedOut
										? {
												opacity: 0.38, // disabled opacity
												textDecorationLine: "line-through",
											}
										: {}),
								},
							}}
						/>
					)}
					right={(p) => (
						<Box
							{...p}
							height={34}
							minWidth={60}
							flexDirection="row"
							justifyContent="flex-end"
							alignItems="center"
						>
							{answered && percentageAnswered ? (
								<PercentageAnsweredChip value={percentageAnswered} />
							) : null}
							{!answered && showCrossOut && !drag && (
								<CrossOutAnswerIconButton {...p} isCrossedOut={isCrossedOut} />
							)}
							{answered && drag && (
								<Icon
									name={
										isCorrectAnswer
											? "check-circle-outline"
											: "close-circle-outline"
									}
									color={
										isCorrectAnswer
											? theme.colors.success.main
											: theme.colors.confidence.low
									}
									size={24}
								/>
							)}
							{!answered && drag && (
								<Icon
									name="drag-vertical"
									color={theme.colors.onSurfaceVariant}
									size={24}
									style={{
										...(isWeb
											? {
													cursor: "grab",
												}
											: {}),
									}}
								/>
							)}
						</Box>
					)}
					testID={`choice-list-item-${index}`}
				/>
			</ListItemContainer>
		</Wrapper>
	);
};

export default ChoiceListItem;
